import { proxy, useSnapshot } from 'valtio'
import { proxyMap, proxySet } from 'valtio/utils'

import { type Comment, mapDTOtoComment } from '../models/Comment'
import { listCommentReactions, listComments } from '../api/commentApi'
import {
  getLiveId,
  getPresenterToken,
  getRole,
} from '../../global/blocs/roomInfo'
import { getLiveToken } from '../../global/blocs/useConnectInfo'
import { subscribe } from '../../realtime/realtime'
import { getLocalSpeakerName } from '../../webrtc/webRTCRoom'

import { type ChatId } from './chatVisibility'

type ChatMessagePaginationState = {
  isFetchingNextPage: boolean
}

type ChatMessagesState = Readonly<{
  ids: Readonly<{
    'speaker-chat': (number | undefined)[] | undefined
    chat: (number | undefined)[] | undefined
  }>
  messages: Map<number, Comment>
  reactedTo: Map<number, Set<string>> | undefined
  pagination: {
    chat: ChatMessagePaginationState
    'speaker-chat': ChatMessagePaginationState
  }
}>

const chatMessagesState = proxy<ChatMessagesState>({
  ids: {
    'speaker-chat': undefined,
    chat: undefined,
  },
  messages: proxyMap(),
  reactedTo: undefined,
  pagination: {
    chat: {
      isFetchingNextPage: false,
    },
    'speaker-chat': {
      isFetchingNextPage: false,
    },
  },
})

// ===== Fetching data =====

const getRemoteChatMessage = async (commentId: number) => {
  const response = await listComments({
    object_type: 'live',
    object_id: getLiveId(),
    include_reactions_p: true,
    include_reply_to_comments_p: true,
    presenter_token: getPresenterToken(),
    comment_id: commentId,
  })

  const dto = response.data.at(0)
  if (!dto) return
  // return if data is empty to avoid runtime errors
  const comment = mapDTOtoComment(dto)
  updateChatMessage(response.data[0].comment_type, commentId, comment)
}

const getRemoteReactions = async () => {
  const response = await listCommentReactions({
    object_id: getLiveId(),
    object_type: 'live',
    object_token: await getLiveToken(),
  })

  if (!chatMessagesState.reactedTo) {
    // @ts-expect-error — this is the initial write
    chatMessagesState.reactedTo = proxyMap()
  }

  chatMessagesState.reactedTo!.clear()

  response.data.forEach(({ comment_id, reaction_emoji }) => {
    const r = chatMessagesState.reactedTo!.get(comment_id)
    if (r) {
      r.add(reaction_emoji)
    } else {
      chatMessagesState.reactedTo!.set(comment_id, proxySet([reaction_emoji]))
    }
  })
}

const RESERVED_BOT_GREETING_MESSAGE_ID = -1
const RESERVED_WEBINAR_INFO_MESSAGE_ID = -3

let injectedMessages = 0

const PAGE_SIZE = 100
const getNextPage = async (type: ChatId) => {
  const messageIds = chatMessagesState.ids[type]

  let page
  if (messageIds?.length) {
    const lastUnfetched = messageIds.findLastIndex((v) => v === undefined)
    page = Math.floor(lastUnfetched / PAGE_SIZE)
  } else {
    page = -1 // first page
  }

  chatMessagesState.pagination[type].isFetchingNextPage = true

  const response = await listComments({
    object_type: 'live',
    comment_type: type,
    object_id: getLiveId(),
    include_reactions_p: true,
    include_reply_to_comments_p: true,
    presenter_token: getPresenterToken(),
    prioritize_promoted_p: false,
    order: page === -1 ? 'desc' : 'asc',
    size: page === -1 ? PAGE_SIZE * 2 : PAGE_SIZE,
    p: page === -1 ? 1 : page + 1,
  })

  const comments = response.data?.map(mapDTOtoComment)
  const currentPage = response.p

  if (!chatMessagesState.ids[type]) {
    // @ts-expect-error — this is the initial write
    chatMessagesState.ids[type] = new Array(response.total_count).fill(
      undefined
    )
  }

  // TODO: used for debug; remove
  const injectedMessagesForChatType = type === 'chat' ? injectedMessages : 0
  if (
    chatMessagesState.ids[type].length !==
    response.total_count + injectedMessagesForChatType
  ) {
    console.error(
      'Wrong message count in chat',
      type,
      chatMessagesState.ids[type].length,
      response.total_count,
      injectedMessagesForChatType
    )
  }

  comments.forEach((c) => chatMessagesState.messages.set(c.id, c))

  if (page === -1) {
    chatMessagesState.ids[type]?.splice(
      chatMessagesState.ids[type].length - comments.length,
      comments.length,
      ...comments.reverse().map((c) => c.id)
    )
  } else {
    chatMessagesState.ids[type]?.splice(
      page * PAGE_SIZE,
      comments.length,
      ...comments.map((c) => c.id)
    )
  }

  // Add reserved messages
  if (currentPage === 1) {
    switch (type) {
      case 'chat':
        if (comments?.length === 0 && getRole() !== 'producer') {
          chatMessagesState.ids[type].push(RESERVED_BOT_GREETING_MESSAGE_ID)
          injectedMessages += 1
        }
        chatMessagesState.ids[type].push(RESERVED_WEBINAR_INFO_MESSAGE_ID)
        injectedMessages += 1
        break
      case 'speaker-chat':
      default:
    }
  }

  chatMessagesState.pagination[type].isFetchingNextPage = false
}

// ===== Pagination  =====

const isFetchingNextPage = (type: ChatId) =>
  chatMessagesState.pagination[type].isFetchingNextPage

// ====================

const updateMessageId = (type: ChatId, commentId: number, newId: number) => {
  if (chatMessagesState.ids[type]) {
    const oldCommentIdIndex = chatMessagesState.ids[type].lastIndexOf(commentId)

    if (oldCommentIdIndex !== -1) {
      if (!chatMessagesState.ids[type].includes(newId)) {
        chatMessagesState.ids[type][oldCommentIdIndex] = newId
      } else {
        // If the newId already exists, we need to remove the old commentId
        chatMessagesState.ids[type].splice(oldCommentIdIndex, 1)
      }
    }
  }

  const oldCommentContent = chatMessagesState.messages.get(commentId)
  if (oldCommentContent) {
    chatMessagesState.messages.set(newId, {
      ...oldCommentContent,
      isOptimisticUpdate: false,
    })
    chatMessagesState.messages.delete(commentId)
  }

  const oldCommentReactions = chatMessagesState.reactedTo?.get(commentId)
  if (oldCommentReactions) {
    chatMessagesState.reactedTo?.set(newId, oldCommentReactions)
    chatMessagesState.reactedTo?.delete(commentId)
  }
}

const addMessage = async (type: ChatId, message: string, replyId?: number) => {
  let senderName = window.localStorage?.comment_name ?? 'You'
  let fullName = window.localStorage?.comment_full_name

  if (getRole() !== 'audience') {
    const names = await getLocalSpeakerName()
    senderName = names?.name
    fullName = names?.fullName
  }

  const replyMessageContent = replyId
    ? chatMessagesState.messages.get(replyId)
    : undefined

  const id = Math.random()

  const ids = chatMessagesState.ids[type]

  if (ids) {
    ids.push(id)
    chatMessagesState.messages.set(id, {
      id,
      htmlContent: message,
      textContent: message,
      name: senderName,
      fullName,
      pinned: false,
      label: getRole() === 'audience' ? 'audience' : 'speaker',
      reactions: proxyMap(),
      reply:
        replyId && replyMessageContent
          ? {
              commentId: replyId,
              name: replyMessageContent.name,
              fullName: replyMessageContent.fullName,
              label: replyMessageContent.label,
              content: replyMessageContent.textContent,
            }
          : undefined,
      isOptimisticUpdate: true,
    })
  }

  return id
}

const deleteMessage = (commentId: number) => {
  const chatMessagesIds = chatMessagesState.ids.chat
  const chatIndex = chatMessagesIds?.lastIndexOf(commentId) ?? -1
  if (chatIndex !== -1) {
    chatMessagesIds?.splice(chatIndex, 1)
  }

  const speakerChatMessagesIds = chatMessagesState.ids['speaker-chat']
  const speakerChatIndex = speakerChatMessagesIds?.lastIndexOf(commentId) ?? -1
  if (speakerChatIndex !== -1) {
    speakerChatMessagesIds?.splice(speakerChatIndex, 1)
  }

  chatMessagesState.messages.delete(commentId)
  chatMessagesState.reactedTo?.delete(commentId)
}

const pinMessage = (commentId: number, pin: boolean) => {
  const comment = chatMessagesState.messages.get(commentId)
  if (comment) comment.pinned = pin
}

const addReaction = (commentId: number, emoji: string) => {
  // Update the reactions of the current user
  const { reactedTo } = chatMessagesState
  if (reactedTo) {
    const commentReactions = reactedTo.get(commentId)
    if (commentReactions) {
      commentReactions.add(emoji)
    } else {
      reactedTo.set(commentId, proxySet(emoji))
    }
  }

  // Update the reactions of the message
  const message = chatMessagesState.messages.get(commentId)
  message?.reactions.set(emoji, (message.reactions.get(emoji) ?? 0) + 1)
}

const removeReaction = (commentId: number, emoji: string) => {
  // Update the reactions of the current user
  chatMessagesState.reactedTo?.get(commentId)?.delete(emoji)

  // Update the reactions of the message
  const message = chatMessagesState.messages.get(commentId)
  const newValue = (message?.reactions.get(emoji) ?? 1) - 1
  if (newValue > 0) {
    message?.reactions.set(emoji, newValue)
  } else {
    message?.reactions.delete(emoji)
  }
}

const updateChatMessage = (
  type: ChatId,
  messageId: number,
  message: Comment
) => {
  chatMessagesState.messages.set(messageId, message)
  const ids = chatMessagesState.ids[type]
  if (!ids || ids.includes(messageId)) return

  ids.push(messageId)
}

// ===== Hooks  =====

const initializedMessages = {
  chat: false,
  'speaker-chat': false,
}

const useChatMessages = (type: ChatId) => {
  if (!chatMessagesState.ids[type] && !initializedMessages[type]) {
    subscribeToRealtimeUpdates()
    initializedMessages[type] = true
    getNextPage(type).catch(() => {
      initializedMessages[type] = false
    })
  }
  return useSnapshot(chatMessagesState).ids[type]
}

const useChatMessage = (commentId: number) => {
  return useSnapshot(chatMessagesState).messages.get(commentId)
}

let initializedReactions = false
const useOwnReactions = (commentId: number) => {
  if (!chatMessagesState.reactedTo && !initializedReactions) {
    initializedReactions = true
    getRemoteReactions().catch(() => {
      initializedReactions = false
    })
  }
  return useSnapshot(chatMessagesState).reactedTo?.get(commentId)
}

const useHasNextPage = (type: ChatId) => {
  const messages = useSnapshot(chatMessagesState).ids[type]

  if (!messages) return false

  const firstEmptyMessage = messages.findLastIndex((c) => c === undefined)
  return firstEmptyMessage !== -1
}

// ===== Realtime  =====

type RealtimeMessage =
  | {
      event: 'comment_update'
      payload?: {
        comment_id: string
      }
    }
  | {
      event: 'delete'
      comment_id: string
    }
  | {
      event: 'add' | 'promote' | 'unpromote'
      avatar: string
      comment_id: string // '8866692'
      comment_type: ChatId // 'chat'
      entry_date: string // '1744148148'
      label: string // 'Speaker'
      object_id: string // '8864452'
      promoted_date: string // ''
      promoted_p: string // '0'
      user_id: string // '8063744'
      json: {
        avatar: string // '/files/8864463/speaker-avatar.png'
        buddy_icon: string // '/files/8864463/speaker-avatar.png'
        comment_id: string // '8866692'
        comment_status: string // ''
        comment_type: ChatId // 'chat'
        content: string // '4'
        content_text: string // '4'
        entry_date: string // '2025-04-08 21:35:48.045618+00'
        entry_date_ansi: string // '2025-04-08 23:35:48'
        facebook_link: string // '/live/8864452/facebook?comment_id=8866692'
        html: string // '\n\n<div id="feedcard8866692" class="feed-item feed-item-chat">\n  <div class="panel panel-default">\n    <div class="panel-body feed-body">\n      <div class="feed-content">\n        \n          \n          <p class="description">4</p>\n          \n        \n      </div>\n    </div>\n    <div class="panel-footer">\n      <div class="feed-meta">\n        <!--  -->\n        \n        <!-- 08 April 2025 -->\n        23:35\n\n        <div class="feed-controls">\n          <a href="/live/8864452/facebook?comment_id=8866692" class="facebook" title="Facebook"><span class="fa fa-facebook"></span></a>\n          <a href="/live/8864452/twitter?comment_id=8866692" class="twitter" title="Twitter"><span class="fa fa-twitter"></span></a>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n'
        label: string // 'Speaker'
        media: {
          description: string // 'Disse adidas Handball Spezial-sko er et arkivinspireret livsstilsdesign. De har en overdel i læder og syntetisk materiale samt en ydersål i gummi, der er skabt til at bevæge sig behageligt mellem afslappede hygge og diskrete eventyr. Det klassiske look henter sin inspiration fra fortidens håndboldbaner og er opdateret med moderne detaljer. En forstærket tå og hæl giver ekstra holdbarhed, så du kan holde de gode tider i gang.'
          provider_name: string // 'adidas Danmark'
          provider_url: string // 'https://www.adidas.dk'
          thumbnail_height: string // '840'
          thumbnail_url: string // 'https://i.embed.ly/1/image?url=https%3A%2F%2Fassets.adidas.com%2Fimages%2Fh_840%2Cf_auto%2Cq_auto%2Cfl_lossy%2Cc_fill%2Cg_auto%2Faee7039309304e29a56e62574ca5ea2f_9366%2FHandball_Spezial_sko_Hvid_JH5451_01_00_standard.jpg&key=c0f0e109d6484df8a5c876cd4c15a4fa'
          thumbnail_width: string // '840'
          title: string // 'adidas Handball Spezial sko - Hvid | adidas Denmark'
          type: string // 'link'
          url: string // 'https://www.adidas.dk/handball-spezial-sko/JH5451.html?pr=taxonomy_rr&slot=8&rec=mt'
          version: string // '1.0'
        }[]
        name: string // 'João Ferreira'
        object_id: string // '8864452'
        object_type: string // 'live'
        pretty_date: string // '08 April 2025'
        pretty_time: string // '23:35'
        promoted_p: string // 'f'
        short_date: string // ' 8. Apr'
        site_id: string // '4701703'
        truncated_content: string // '4'
        truncated_name: string // 'João Ferreira'
        twitter_link: string // '/live/8864452/twitter?comment_id=8866692'
        url: string // ''
        user_id: string // '8063744'
      }
    }

const subscribed = false
const subscribeToRealtimeUpdates = () => {
  if (subscribed) return

  subscribe('public', '/all_json', (message: string) => {
    const event = JSON.parse(message) as RealtimeMessage

    switch (event.event) {
      case 'comment_update':
        if (event?.payload) {
          getRemoteChatMessage(+event.payload.comment_id)
        }
        break
      case 'promote':
        pinMessage(+event.comment_id, true)
        break
      case 'unpromote':
        pinMessage(+event.comment_id, false)
        break
      case 'add':
        if (event.comment_id) {
          // TODO: use message pages, instead of single messages
          getRemoteChatMessage(+event.comment_id)
        }
        break
      case 'delete':
        deleteMessage(+event.comment_id)
        break
      default:
    }
  })
}

export {
  getNextPage,
  isFetchingNextPage,
  updateMessageId,
  addMessage,
  deleteMessage,
  pinMessage,
  addReaction,
  removeReaction,
  useChatMessages,
  useChatMessage,
  useOwnReactions,
  useHasNextPage,
  RESERVED_WEBINAR_INFO_MESSAGE_ID,
  RESERVED_BOT_GREETING_MESSAGE_ID,
}
